// src/hooks/useNotifications.js
import { useCallback, useRef } from 'react';

export const useNotifications = (enabled = true) => {
  const audioContextRef = useRef(null);

  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const playNotificationSound = useCallback((type = 'info', priority = 'medium') => {
    if (!enabled) return;

    try {
      const ctx = initAudioContext();
      
      // Sound configurations
      const soundConfig = {
        info: [523.25, 659.25],      // C5, E5
        alert: [880, 1046.5, 880],   // A5, C6, A5
        success: [523.25, 659.25, 783.99], // C5, E5, G5
        error: [392, 329.63, 261.63], // G4, E4, C4
      };

      const frequencies = soundConfig[type] || soundConfig.info;
      const duration = priority === 'critical' ? 0.4 : 0.25;
      const volume = priority === 'critical' ? 0.15 : 0.1;

      frequencies.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.frequency.value = freq;
        osc.type = priority === 'critical' ? 'sawtooth' : 'sine';

        const startTime = ctx.currentTime + (index * 0.1);
        
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(volume, startTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

        osc.start(startTime);
        osc.stop(startTime + duration);
      });
    } catch (error) {
      console.warn('Audio playback failed:', error);
    }
  }, [enabled, initAudioContext]);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }, []);

  const showNotification = useCallback((data) => {
    if (!enabled || !('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    // Handle both old format (with title/body) and new format (raw data)
    let title, body, tag, requireInteraction;

    if (data.title) {
      // Simple format: { title, body, tag }
      title = data.title;
      body = data.body || '';
      tag = data.tag || 'notification';
      requireInteraction = data.requireInteraction || false;
    } else {
      // Complex format: full data object
      const getEmoji = (type) => {
        switch (type) {
          case 'alert_triggered': return '🔔';
          case 'condition_update': return '📊';
          case 'buy': return '📈';
          case 'sell': return '📉';
          default: return '⚡';
        }
      };

      title = `${getEmoji(data.type)} ${data.strategy_id || 'Alert'}`;
      
      const bodyParts = [];
      if (data.alerts && data.alerts.length > 0) {
        bodyParts.push(`✅ ${data.alerts.length} condition(s) met`);
        data.alerts.forEach(alert => {
          if (alert.signal_data) {
            const trigger = alert.signal_data.trigger || 'Trigger';
            const target = alert.signal_data.target || 'Target';
            const elapsed = alert.signal_data.elapsed_minutes || '?';
            bodyParts.push(
              `${alert.condition_name}: ${trigger} → ${target} (${elapsed}min)`
            );
          }
        });
      }
      
      if (data.timestamp) {
        bodyParts.push(`⏰ ${new Date(data.timestamp).toLocaleTimeString()}`);
      }

      body = bodyParts.join('\n');
      tag = `alert-${data.strategy_id || Date.now()}`;
      requireInteraction = data.alerts && data.alerts.length > 0;
    }

    const options = {
      body: body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: tag,
      requireInteraction: requireInteraction,
      silent: false,
      timestamp: Date.now(),
      data: {
        url: window.location.href,
        ...data
      },
    };

    try {
      const desktopNotification = new Notification(title, options);

      if (!requireInteraction) {
        setTimeout(() => {
          desktopNotification.close();
        }, 8000);
      }

      desktopNotification.onclick = () => {
        window.focus();
        desktopNotification.close();
      };

      return desktopNotification;
    } catch (error) {
      console.warn('Failed to show notification:', error);
    }
  }, [enabled]);

  return {
    requestPermission,
    showNotification,
    playNotificationSound,
    isSupported: 'Notification' in window,
    permission: typeof Notification !== 'undefined' ? Notification.permission : 'default',
  };
};
